import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../CartContext";
import { enqueueSnackbar } from "notistack";
import BackButton from "../../Components/BackButton";
import { _post, _get } from "../../services/apiClient";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../helper/formatNumber";
import { useRestaurantDetails } from "../../context/RestaurantDetailsContext";
import { ShopInfo } from "../../shopinfo";
import confetti from "canvas-confetti";
import CartItems from "./CartItems";
import OrderSummary from "./OrderSummary";
import OrderSuccess from "./OrderSuccess";
import ServiceUnavailable from "../../Components/ServiceUnavailable";
import { getAppConfig } from "../../utilis/appConfig";
import { hasValidCustomerLogin, goToLogin } from "../../utilis/customerSession";
import {
  saveGuestDetails,
  loadGuestDetails,
  clearGuestDetails,
  hasGuestDetails,
} from '../../utilis/guestStorage';

const Cart = ({
  cart,
  setCart,
  form,
  setForm,
  phoneNumber,
  loggedIn,
  restaurantStatus,
  isDineIn = false,
  onClose,
  joinToken,
  setJoinToken,
}) => {
  const {
    updateQuantity,
    itemQuantities,
    resetItemQuantities,
    setItemQuantities,
  } = useCart();
  const totalQuantity = Object.values(itemQuantities).reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );
  const [show, setShow] = useState(false);
  const [orderSummary, setOrderSummary] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [finalTotal, setFinalTotal] = useState(0);
  const { t, i18n } = useTranslation();

  const { restaurantDetails } = useRestaurantDetails();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [addressErrors, setAddressErrors] = useState({
    houseNo: "",
    area: "",
    pincode: "",
  });
  const [jwtUser, setJwtUser] = useState(null);
  const [recentAddresses, setRecentAddresses] = useState([]);

  // Dine-in specific state
  const [allocationId, setAllocationId] = useState(null);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [showPreviousOrders, setShowPreviousOrders] = useState(true);

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [locationPermission, setLocationPermission] = useState("unknown");



  const restaurantId = restaurantDetails?._id;
  const locale = restaurantDetails?.locale || "en-IN";
  const gstRate = restaurantDetails?.gstRate ?? 0;

  const shop = ShopInfo[i18n.language] || ShopInfo.en;

  // Separate new items and ordered items for dine-in mode
  const newItems = isDineIn ? cart.filter((item) => !item.readonly) : cart;
  const readonlyItems = isDineIn ? cart.filter((item) => item.readonly) : [];

  const taxType = restaurantDetails?.taxType || "gst";
  const taxLabel = t(taxType);
  const WHATSAPP_REDIRECT =
    process.env.REACT_APP_WHATSAPP_REDIRECT === "true";

  const readonlyItemQuantities = React.useMemo(() => {
    return readonlyItems.reduce((acc, item) => {
      acc[item._id] = { quantity: item.quantity || 0 };
      return acc;
    }, {});
  }, [readonlyItems]);

  // Calculate totals
  const itemTotal = newItems.reduce((sum, item) => {
    const variant = item.variants?.[0];
    // Prefer the stored line price (bakes in any selected add-ons) over
    // recomputing from the base product fields.
    const price =
      itemQuantities[item._id]?.price ??
      variant?.offerPrice ??
      variant?.price ??
      item.offerPrice ??
      item.price;
    const quantity = itemQuantities[item._id]?.quantity || 0;
    return sum + price * quantity;
  }, 0);

  const orderedItemsTotal = isDineIn
    ? readonlyItems.reduce((sum, item) => {
      const variant = item.variants?.[0];
      const price =
        variant?.offerPrice ??
        variant?.price ??
        item.offerPrice ??
        item.price;
      const quantity = item.quantity || 0;
      return sum + price * quantity;
    }, 0)
    : 0;

  const totalPackageCharge = !isDineIn
    ? [...newItems, ...readonlyItems].reduce((sum, item) => {
      const packageCharge = Number(item.packageCharge || 0);
      if (packageCharge <= 0) return sum;

      const quantity = itemQuantities[item._id]?.quantity || item.quantity || 0;
      return sum + packageCharge * quantity;
    }, 0)
    : 0;


  const totalItemsAmount = itemTotal + orderedItemsTotal;
  const gstAmount = isDineIn
    ? 0
    : newItems.reduce((sum, item) => {
      const variant = item.variants?.[0];
      const price =
        itemQuantities[item._id]?.price ??
        variant?.offerPrice ??
        variant?.price ??
        item.offerPrice ??
        item.price;

      const quantity = itemQuantities[item._id]?.quantity || 0;

      if (item.isTaxable !== true) return sum;

      return sum + price * quantity * gstRate;
    }, 0);
  const totalWithGST =
    totalItemsAmount + gstAmount + totalPackageCharge;

  // Always start at the top when cart page mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    // For dine-in mode, load allocation and placed items
    if (isDineIn) {
      const storedAllocationId = localStorage.getItem("allocationId");
      if (storedAllocationId) {
        setAllocationId(storedAllocationId);
        loadPlacedItems(storedAllocationId);
        checkAllocationStatus(storedAllocationId);
      }
    }

    if (!token) {
      // NEW: Load guest details from localStorage (only for non-logged-in users)
      if (!isDineIn) {
        const savedGuestDetails = loadGuestDetails(loggedIn);
        if (Object.keys(savedGuestDetails).length > 0) {
          setForm((prev) => ({
            ...prev,
            phoneNumber: savedGuestDetails.phoneNumber || prev.phoneNumber || "",
            name: savedGuestDetails.name || prev.name || "",
            email: savedGuestDetails.email || prev.email || "",
            houseNo: savedGuestDetails.houseNo || prev.houseNo || "",
            area: savedGuestDetails.area || prev.area || "",
            landmark: savedGuestDetails.landmark || prev.landmark || "",
            pincode: savedGuestDetails.pincode || prev.pincode || "",
          }));
        }
      }
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setJwtUser(decoded);

      if (!decoded?.id) return;

      const fetchUserDetails = async () => {
        try {
          const res = await _get("/auth/me");
          if (res.data?.success) {
            setForm((prev) => ({
              ...prev,
              userId: decoded.id,
              phoneNumber: res.data.user.phoneNumber || "",
              name: res.data.user.name || "",
              email: res.data.user.email || "",
            }));
          }
        } catch (err) {
          console.error("Failed to fetch user:", err);
        }
      };

      const fetchRecentAddresses = async () => {
        try {
          const res = await _get("/orders/address/me");
          setRecentAddresses(res.data || []);
        } catch (err) {
          console.error("Failed to fetch recent addresses:", err);
        }
      };

      fetchUserDetails();
      if (!isDineIn) {
        fetchRecentAddresses();
      }
    } catch (err) {
      console.error("Invalid JWT:", err);
    }
  }, [setForm, isDineIn, loggedIn]);

  const loadPlacedItems = async (allocId) => {
    try {
      const response = await _get(
        `/orders/customerOrder/by-allocation/${allocId}`,
      );
      if (response.data && Array.isArray(response.data.items)) {
        const placedItems = response.data.items.map((item, index) => ({
          ...item,
          _id: item._id || `${item.productId}_${index}`, // Ensure unique _id for readonly items
          readonly: true,
        }));

        // Remove old readonly items and add fresh ones to prevent duplicates
        setCart((prev) => {
          const nonReadonlyItems = prev.filter((item) => !item.readonly);
          return [...nonReadonlyItems, ...placedItems];
        });

        if (response.data.joinToken && setJoinToken) {
          setJoinToken(response.data.joinToken);
        }
      }
    } catch (error) {
      console.error("Error loading placed items:", error);
    }
  };

  const checkAllocationStatus = async (allocId) => {
    try {
      const response = await _get(`/allocation/status/${allocId}`);
      if (response.data) {
        setAllocationStatus(response.data.status);
      }
    } catch (error) {
      console.error("Error checking allocation status:", error);
    }
  };

  const removeItem = (_id) => {
    const item = cart.find((item) => item._id === _id && !item.readonly);

    if (!item) {
      const readonlyItem = cart.find((item) => item._id === _id && item.readonly);
      if (readonlyItem) {
        enqueueSnackbar("Cannot remove already placed items", {
          variant: "warning",
        });
      }
      return;
    }

    const variant = item.variants?.[0];
    const price =
      variant?.offerPrice ?? variant?.price ?? item.offerPrice ?? item.price;
    updateQuantity(_id, 0, price);

    const updatedCart = cart.filter((item) => item._id !== _id || item.readonly);
    setCart(updatedCart);
    sessionStorage.setItem("cartValue", JSON.stringify(updatedCart));
  };

  const handleNoteChange = (_id, note) => {
    const updatedCart = cart.map((item) =>
      item._id === _id ? { ...item, note } : item,
    );
    setCart(updatedCart);
    sessionStorage.setItem("cartValue", JSON.stringify(updatedCart));
  };

  const handleShare = async () => {
    if (!allocationId) {
      enqueueSnackbar("No active table session", { variant: "error" });
      return;
    }

    try {
      // The table comes from the table session token
      const response = await _post("/allocation/share-link");
      if (response.data?.token) {
        const baseUrl = window.location.origin;
        const fullShareLink = `${baseUrl}/?share=${response.data.token}`;

        // Use Web Share API if available
        if (navigator.share) {
          try {
            await navigator.share({
              title: t("shareTableOrder") || "Join Table Order",
              text:
                t("shareTableOrderText") || "Join my table and order together!",
              url: fullShareLink,
            });
            enqueueSnackbar(t("sharedSuccessfully") || "Shared successfully!", {
              variant: "success",
            });
          } catch (shareError) {
            // User cancelled share or share failed
            if (shareError.name !== "AbortError") {
              console.error("Error sharing:", shareError);
              // Fallback to copy link
              navigator.clipboard.writeText(fullShareLink);
              enqueueSnackbar(t("linkCopied") || "Link copied to clipboard!", {
                variant: "success",
              });
            }
          }
        } else {
          // Fallback for browsers that don't support Web Share API
          navigator.clipboard.writeText(fullShareLink);
          enqueueSnackbar(t("linkCopied") || "Link copied to clipboard!", {
            variant: "success",
          });
        }
      }
    } catch (error) {
      console.error("Error generating share link:", error);
      enqueueSnackbar("Failed to generate share link", { variant: "error" });
    }
  };

  const validateAddress = () => {
    const errors = {};

    if (!form.houseNo || !form.houseNo.trim()) {
      errors.houseNo = "House / Flat No. is required";
    }

    if (!form.area || !form.area.trim()) {
      errors.area = "Area is required";
    }

    if (!form.pincode || form.pincode.trim() === "") {
      errors.pincode = "Pincode is required";
    }

    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 500,
      spread: 500,
      origin: { y: 0.6 },
      colors: ["#ff0", "#f00", "#0f0", "#00f", "#f0f"],
    });
  };

  const handlePlaceOrder = async () => {
    if (restaurantStatus === "offline") {
      enqueueSnackbar("The restaurant is not accepting any orders right now!", {
        variant: "error",
      });
      return;
    }

    if (!isDineIn) {
      // Config-driven (Admin → Configure → "Login Required to Order")
      if (getAppConfig().loginRequiredToOrder && !hasValidCustomerLogin()) {
        enqueueSnackbar("Please log in to place your order.", { variant: "info" });
        goToLogin(navigate, window.location.pathname + window.location.search);
        return;
      }

      //  Mobile number validation
      if (!form.phoneNumber || !form.phoneNumber.trim()) {
        enqueueSnackbar("Please enter mobile number", {
          variant: "error",
        });
        return;
      }


      if (!validateAddress()) {
        enqueueSnackbar("Please fill in all required address fields", {
          variant: "error",
        });
        return;
      }
    } else {
      let guestInfo = {};
      try {
        guestInfo = JSON.parse(localStorage.getItem("guestInfo") || "{}");
      } catch (e) {
        guestInfo = {};
      }
      const guestName = String(guestInfo.name || "").trim();
      if (!guestName) {
        enqueueSnackbar("Please enter your name", {
          variant: "error",
        });
        return;
      }
      const guestCount = Number(guestInfo.customerCount);
      if (!guestCount || guestCount < 1 || guestCount > 15) {
        enqueueSnackbar("Please enter guest count", {
          variant: "error",
        });
        return;
      }
      const phoneDigits = String(guestInfo.phone || "").replace(/\D/g, "");
      const PLACEHOLDER_PHONES = new Set(["9999999999", "0999999999"]);
      if (!phoneDigits || phoneDigits.length !== 10) {
        enqueueSnackbar("Please enter your mobile number", {
          variant: "error",
        });
        return;
      }
      if (PLACEHOLDER_PHONES.has(phoneDigits)) {
        enqueueSnackbar(
          "Please start a new table session and enter your real mobile number (placeholder numbers are not allowed).",
          { variant: "error", autoHideDuration: 6000 },
        );
        return;
      }
    }

    setIsPlacingOrder(true);

    try {
      if (isDineIn) {
        await handleDineInOrder();
      } else {
        await handleDeliveryOrder();
      }
    } catch (error) {
      console.error("Error placing order:", error.response?.data || error);

      // Handle specific error messages
      const errorMessage = error.response?.data?.message || error.message;

      if (errorMessage?.includes("restaurant accepts the request")) {
        enqueueSnackbar(
          "Please wait for the restaurant to accept your table request before placing an order.",
          { variant: "warning", autoHideDuration: 5000 },
        );
      } else if (["FEATURE_OFF", "RESTAURANT_OFFLINE"].includes(error.response?.data?.code)) {
        // Ordering switched off by the restaurant
        enqueueSnackbar(errorMessage, { variant: "error" });
      } else if (String(error.response?.data?.code || "").startsWith("TABLE_SESSION")) {
        // Table session expired or ended: the customer must scan the QR again
        enqueueSnackbar(errorMessage, { variant: "warning", autoHideDuration: 6000 });
      } else if (error.response?.data?.code === "LOGIN_REQUIRED") {
        // Server requires login (setting just turned on, or login expired)
        enqueueSnackbar(errorMessage, { variant: "info" });
        goToLogin(navigate, window.location.pathname + window.location.search);
      } else if (error.response?.status === 429) {
        // Server spike-arrest limit: show its "please wait" message
        enqueueSnackbar(errorMessage, { variant: "warning", autoHideDuration: 6000 });
      } else if (error.response?.status === 403) {
        if (errorMessage?.includes("AMC is not active")) {
          enqueueSnackbar(errorMessage, { variant: "error" });
        } else {
          enqueueSnackbar(
            "Access denied. Please ensure your table session is active.",
            { variant: "error" },
          );
        }
      } else {
        enqueueSnackbar("Error placing order. Try again!", {
          variant: "error",
        });
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleDineInOrder = async () => {
    const guestInfo = JSON.parse(localStorage.getItem("guestInfo") || "{}");
    const username = String(guestInfo.name || "").trim();

    const items = newItems.map((item) => {
      const variant = item.variants?.[0];
      const price =
        itemQuantities[item._id]?.price ??
        variant?.offerPrice ??
        variant?.price ??
        item.offerPrice ??
        item.price;
      const quantity = itemQuantities[item._id]?.quantity || 0;

      return {
        productId: item._id,
        productName:
          typeof item.productName === "string"
            ? item.productName
            : item.productName?.en || "",
        price: price,
        quantity: quantity,
        note: item.note || "",
        addOns: item.addOns || [],
      };
    });

    const orderData = {
      allocationId,
      username,
      items,
    };

    const response = await _post("/orders/customerOrder/placed", orderData);

    if (response.data) {
      enqueueSnackbar("Order placed successfully!", {
        variant: "success",
      });

      // Remove newly placed items from itemQuantities (clears cart icon count)
      setItemQuantities((prev) => {
        const updated = { ...prev };
        newItems.forEach((item) => {
          delete updated[item._id];
        });
        sessionStorage.setItem("cartQuantityValue", JSON.stringify(updated));
        return updated;
      });

      // Clear new items from cart, keep only previous readonly items
      const updatedCart = cart.filter((item) => item.readonly);
      setCart(updatedCart);
      sessionStorage.setItem("cartValue", JSON.stringify(updatedCart));

      if (allocationId) {
        loadPlacedItems(allocationId);
      }
    }
  };

  const handleDeliveryOrder = async () => {
    //  console.log("🟢 Restaurant Details:", restaurantDetails);
    // console.log("🟢 Computed restaurantId:", restaurantId);

    const products = newItems.map((item) => {
      const variant = item.variants?.[0];
      const price =
        itemQuantities[item._id]?.price ??
        variant?.offerPrice ??
        variant?.price ??
        item.offerPrice ??
        item.price;
      const quantity = itemQuantities[item._id]?.quantity || 0;

      return {
        productId: item._id,
        productTitle:
          typeof item.productName === "string"
            ? item.productName
            : item.productName?.en || "",
        image: item.imageUrl,
        price,
        quantity,
        packageCharge: Number(item.packageCharge || 0),
        offerPrice: variant?.offerPrice || null,
        subTotal: price * quantity,
        isTaxable: item.isTaxable === true,
        note: item.note || "",
        addOns: item.addOns || [],
      };
    });

    const orderData = {
      userId: form.userId || jwtUser?.id || null,
      name: loggedIn ? form.name : "",
      phoneNumber: form.phoneNumber,
      email: form.email || "",
      address: `${form.houseNo}, ${form.area}, ${form.landmark || ""}`.trim(),
      pincode: form.pincode,

      // subTotal: totalItemsAmount,
      // taxAmount: gstAmount,
      // taxrate: gstRate,
      // packagingTotal: totalPackageCharge,
      // amount: totalWithGST,

      paymentId: "111",
      username: loggedIn ? "USER1" : "guest",
      restaurantId,
      products,
      orderType: "online",
    };
    // console.log("📦 FINAL ORDER DATA BEING SENT:");
    // console.log(JSON.stringify(orderData, null, 2));

    const response = await _post("/orders/create", orderData);

    // console.log("📨 ORDER CREATE RESPONSE:", response.data);
    // const response = await _post("/orders/create", orderData);

    if (response.status >= 200 && response.status < 300) {
      const order = response.data.order || response.data;
      const orderId = response.data.orderId || response.data.order?._id;

      if (!orderId) {
        enqueueSnackbar("Error processing order. Please try again!", {
          variant: "error",
        });
        return;
      }
      if (!loggedIn) {
        saveGuestDetails(form, loggedIn);
      }

      sessionStorage.setItem("OrderId", orderId);
      setOrder(order);
      setOrderId(orderId);
      setOrderSummary(
        newItems.map((item) => ({
          ...item,
          quantity: itemQuantities[item._id]?.quantity || 0,
        })),
      );

      setFinalTotal(totalWithGST);

      setCart([]);
      sessionStorage.removeItem("cartValue");
      sessionStorage.removeItem("cartQuantityValue");
      if (typeof resetItemQuantities === "function") {
        resetItemQuantities();
      }

      let itemsSelected = `${t("whatsappMessageHeader")}\n\n`;
      itemsSelected += `Order ID: ${orderId}\n\n`;

      products.forEach((p, index) => {
        let cleanTitle = p.productTitle.replace(/,?\s*N\/A/gi, "").trim();
        cleanTitle = cleanTitle.replace(/,\s*$/, "");
        itemsSelected += `${index + 1}) ${cleanTitle} - ${p.quantity}\n`;
      });

      itemsSelected += `\n💰 Total: ₹${totalWithGST}\n\nThank you for your order!`;

      if (WHATSAPP_REDIRECT) {
        const whatsappUrl = `https://wa.me/${restaurantDetails?.whatsapp || ""}?text=${encodeURIComponent(itemsSelected)}`;

        const link = document.createElement("a");
        link.href = whatsappUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setShow(true);
      try {
        triggerConfetti();
      } catch (e) {
        console.warn("Confetti error:", e);
      }
      enqueueSnackbar("Order placed successfully!", { variant: "success" });

      setTimeout(() => {
        navigate("/");
      }, 3500);
    } else {
      enqueueSnackbar("Failed to place order. Try again!", {
        variant: "error",
      });
    }
  };
  const checkLocationPermission = async () => {
    if (!navigator.permissions) return "unknown";

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });

      setLocationPermission(permission.state);
      return permission.state;
    } catch {
      return "unknown";
    }
  };

  const handleUseCurrentLocation = async () => {
    const permissionStatus = await checkLocationPermission();

    if (permissionStatus === "denied") {
      enqueueSnackbar(
        "Location is blocked. Please enable it from browser settings.",
        { variant: "warning", autoHideDuration: 6000 }
      );
      return;
    }

    if (!navigator.geolocation) {
      enqueueSnackbar("Geolocation not supported on this device.", {
        variant: "error",
      });
      return;
    }

    setIsFetchingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        console.log("Latitude:", latitude);
        console.log("Longitude:", longitude);
        console.log("Accuracy:", accuracy, "meters");

        // Accuracy validation
        if (accuracy > 200) {
          enqueueSnackbar(
            "Low location accuracy detected. Please enter address manually.",
            { variant: "warning", autoHideDuration: 6000 }
          );
          setIsFetchingLocation(false);
          return;
        }

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );

          const data = await response.json();

          console.log("Full address response:", data);

          if (data && data.address) {
            const address = data.address;

            // Extract area
            const area =
              address.suburb ||
              address.neighbourhood ||
              address.city_district ||
              address.town ||
              address.village ||
              address.city ||
              "";

            // Extract locality if available
            const locality =
              address.locality ||
              address.residential ||
              address.hamlet ||
              "";

            const pincode = address.postcode || "";

            setForm((prev) => ({
              ...prev,

              area: locality ? `${locality}, ${area}` : area,
              pincode,
              latitude,
              longitude,
            }));

            enqueueSnackbar("Location fetched successfully!", {
              variant: "success",
            });
          } else {
            enqueueSnackbar("Address details not found. Please enter manually.", {
              variant: "warning",
            });
          }
        } catch (error) {
          enqueueSnackbar("Unable to fetch address details.", {
            variant: "error",
          });
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        setIsFetchingLocation(false);

        if (error.code === 1) {
          setLocationPermission("denied");
          enqueueSnackbar(
            "Location permission denied. Enable it in browser settings.",
            { variant: "warning" }
          );
        } else if (error.code === 2) {
          enqueueSnackbar("Location unavailable.", {
            variant: "error",
          });
        } else if (error.code === 3) {
          enqueueSnackbar("Location request timed out.", {
            variant: "error",
          });
        } else {
          enqueueSnackbar("Failed to get location.", {
            variant: "error",
          });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };


  if (restaurantDetails && restaurantDetails.amcStatus && restaurantDetails.amcStatus !== "active") {
    return <ServiceUnavailable />;
  }

  if (show && order) {
    return (
      <OrderSuccess
        order={order}
        orderId={orderId}
        orderSummary={orderSummary}
        finalTotal={finalTotal}
        locale={locale}
      />
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-brand-50">
        {/* Header */}
        <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-lg shadow-sm border-b border-brand-200/50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <BackButton />
              <h1 className="text-2xl font-bold text-gray-800">
                {t("yourcart")}
              </h1>
              <span
                className="ms-auto badge rounded-full px-3 py-2 text-sm font-semibold"
                style={{
                  background:
                    "linear-gradient(135deg, rgb(var(--brand-500)) 0%, rgb(var(--brand-600)) 100%)",
                  color: "white",
                }}
              >
                {formatNumber(totalQuantity, i18n.language)} {t("items")}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8 pb-20 max-w-4xl">
          {/* Table Code & Share - Always show for dine-in */}
          {isDineIn && joinToken && (
            <div className="mb-4">
              <div className="bg-white rounded-xl p-3 border border-blue-200 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-0">
                        {t("tableCode") || "Table Code"}
                      </p>
                      <p
                        className="text-xl font-bold text-blue-600 mb-0"
                        style={{ letterSpacing: "0.2em" }}
                      >
                        {joinToken}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(joinToken);
                      enqueueSnackbar(t("codeCopied") || "Code copied!", {
                        variant: "success",
                      });
                    }}
                    className="px-3 py-2 bg-blue-500 text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {t("copy") || "Copy"}
                  </button>
                </div>
                {/* Share Button */}
                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Share Order */}
                  <button
                    onClick={handleShare}
                    className="py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm flex items-center justify-center gap-2"
                  >
                    <i className="feather-share-2"></i>
                    {t("shareOrder") || "Share Order"}
                  </button>

                  {/* Call Waiter */}
                  <button
                    onClick={async () => {
                      try {
                        await _post("/allocation/call-waiter");
                        enqueueSnackbar(t("waiterCalled") || "Waiter notified!", {
                          variant: "success",
                        });
                      } catch (err) {
                        enqueueSnackbar("Failed to call waiter", { variant: "error" });
                      }
                    }}
                    className="py-2 bg-gradient-to-r from-brand-400 to-brand-500 text-white text-sm font-semibold rounded-lg hover:from-brand-500 hover:to-brand-600 transition-all shadow-sm flex items-center justify-center gap-2"
                  >
                    <i className="feather-bell"></i>
                    {t("callWaiter") || "Call Waiter"}
                  </button>
                </div>

              </div>
            </div>
          )}

          {(
            isDineIn
              ? newItems.length === 0 && readonlyItems.length === 0
              : totalQuantity === 0
          ) ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="text-6xl mb-4">🛒</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {t("yourCartIsEmpty")}
              </h2>
              <p className="text-gray-600 mb-6">{t("addItemsToGetStarted")}</p>
              <button
                onClick={() => navigate("/")}
                className="btn btn-lg text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl"
                style={{
                  background:
                    "linear-gradient(135deg, rgb(var(--brand-500)) 0%, rgb(var(--brand-600)) 100%)",
                  border: "none",
                  padding: "0.875rem 2rem",
                }}
              >
                {t("browseMenu")}
              </button>
            </div>
          ) : (
            <div className="row g-4">
              {/* Cart Items */}
              <div className="col-12 col-lg-7 mb-4 mb-lg-0">
                <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6">
                  {isDineIn && newItems.length > 0 && (
                    <>
                      <div className="mb-4 pb-3 border-bottom border-brand-200">
                        <div className="d-flex align-items-center gap-2">
                          <div className="bg-brand-100 rounded-circle p-2">
                            <i
                              className="feather-shopping-bag text-brand-600"
                              style={{ fontSize: "1.2rem" }}
                            ></i>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800 mb-0">
                              {t("currentOrder") || "Current Order"}
                            </h3>
                            <p className="text-sm text-gray-500 mb-0">
                              {newItems.length}{" "}
                              {newItems.length === 1
                                ? t("item") || "item"
                                : t("items") || "items"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <CartItems
                        items={newItems}
                        itemQuantities={itemQuantities}
                        updateQuantity={updateQuantity}
                        removeItem={removeItem}
                        handleNoteChange={handleNoteChange}
                        locale={locale}
                        isDineIn={isDineIn}
                        isReadonly={false}
                      />
                    </>
                  )}

                  {isDineIn && readonlyItems.length > 0 && (
                    <>
                      <div
                        className={`mb-4 pb-3 border-bottom border-green-200 ${newItems.length > 0 ? "mt-6 pt-6 border-top border-gray-200" : ""}`}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            <div className="bg-green-100 rounded-circle p-2">
                              <i
                                className="feather-check-circle text-green-600"
                                style={{ fontSize: "1.2rem" }}
                              ></i>
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-gray-800 mb-0">
                                {t("previousOrders") || "Previous Orders"}
                              </h3>
                              <p className="text-sm text-gray-500 mb-0">
                                {readonlyItems.length}{" "}
                                {readonlyItems.length === 1
                                  ? t("item") || "item"
                                  : t("items") || "items"}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              setShowPreviousOrders(!showPreviousOrders)
                            }
                            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center bg-green-100 border-0 text-green-700 hover:bg-green-200 transition-colors"
                            style={{ width: "36px", height: "36px" }}
                            title={
                              showPreviousOrders
                                ? t("hideOrders") || "Hide"
                                : t("showOrders") || "Show"
                            }
                          >
                            <i
                              className={`feather-chevron-${showPreviousOrders ? "up" : "down"}`}
                              style={{
                                fontSize: "1.2rem",
                                transition: "transform 0.3s",
                              }}
                            ></i>
                          </button>
                        </div>
                      </div>
                      {showPreviousOrders && (
                        <CartItems
                          items={readonlyItems}
                          itemQuantities={readonlyItemQuantities}
                          updateQuantity={updateQuantity}
                          removeItem={removeItem}
                          handleNoteChange={handleNoteChange}
                          locale={locale}
                          isDineIn={isDineIn}
                          isReadonly={true}
                        />
                      )}
                    </>
                  )}

                  {!isDineIn && (
                    <>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 pb-3 border-bottom border-brand-200">
                        {t("itemsInCart")}
                      </h3>
                      <CartItems
                        items={cart}
                        itemQuantities={itemQuantities}
                        updateQuantity={updateQuantity}
                        removeItem={removeItem}
                        handleNoteChange={handleNoteChange}
                        locale={locale}
                        isDineIn={false}
                        isReadonly={false}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Order Summary & Form - Only show when there are items to order */}
              {(!isDineIn || newItems.length > 0) && (
                <div className="col-12 col-lg-5 mb-5 mb-lg-0">
                  <OrderSummary
                    totalItemsAmount={totalItemsAmount}
                    gstAmount={gstAmount}
                    gstRate={gstRate}
                    totalPackageCharge={totalPackageCharge}
                    totalWithGST={totalWithGST}
                    locale={locale}
                    form={form}
                    setForm={setForm}
                    phoneNumber={phoneNumber}
                    loggedIn={loggedIn}
                    addressErrors={addressErrors}
                    jwtUser={jwtUser}
                    recentAddresses={recentAddresses}
                    isDineIn={isDineIn}
                    isPlacingOrder={isPlacingOrder}
                    restaurantStatus={restaurantStatus}
                    handlePlaceOrder={handlePlaceOrder}
                    handleShare={handleShare}
                    joinToken={joinToken}
                    allocationStatus={allocationStatus}
                    handleUseCurrentLocation={handleUseCurrentLocation}
                    isFetchingLocation={isFetchingLocation}
                    locationPermission={locationPermission}
                    taxLabel={taxLabel}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
};

export default Cart;
